import { Certificate, EmploymentType, LocationAutoSuggestItem, LocationData } from '@types';
import { WeekDays } from '../../shared/utils/date-utils';
import { GetBranchOfficesResponse } from '../../hq/services/branch-office-service.types';

export type CompanyData = {
  name: string;
  ortId?: string;
  ortStartDate?: string;
  workCooldownMinutes: number;
  receiptCertificateIds: string[];
  themeCustomizationEnabled?: boolean;
  allowSSOFeatures: boolean;
} & CompanyEmploymentTypes;

export type CompanyUpdateData = Omit<CompanyData, 'name'>;

export type CompanyListItemBasicData = {
  id: string;
  name: string;
} & CompanyEmploymentTypes;

export type CompanyDetail = CompanyListItemBasicData & {
  description: string;
  branchOffice?: GetBranchOfficesResponse['branchOffices'];
  ortId: string | undefined;
  ortName: string | undefined;
  ortStartDate: string | undefined;
  workCooldownMinutes: number;
  receiptCertificates: Certificate[];
  maxFLTravelAllowanceCents: number | undefined;
  maxParkingCents: number | undefined;
  maxTWTravelAllowanceCents: number | undefined;
  maxMileageAllowanceMeters: number | undefined;
  themeCustomizationEnabled: boolean;
  allowSSOFeatures: boolean;
};

type CompanyDetailInOGP = CompanyListItemBasicData & {
  description: string;
  bannerImageUrl: string | null;
  profileImageUrl: string | null;
  archived: boolean;
  locationId: string | null;
  location?: LocationData | LocationAutoSuggestItem;
  socials: Link[];
  orts: OrtsData[];
  requiresEmployeeContract: boolean;
  requiresEmployeeCertificates: boolean;
  employeeMutableUntilMinutes: number;
  offersFreelanceWork: boolean;
  offersTempWork: boolean;
  offersEmployeeWork: boolean;
  themeCustomizationEnabled?: boolean;
  allowShiftStartTimeOverride: boolean;
  allowShiftEndTimeOverride: boolean;
  allowEditCheckoutHours: boolean;
  allowSSOFeatures: boolean;
  isWorkerSSOEnabled: boolean;
  isOgpUserSSOEnabled: boolean;
};

export type CompanyDetailAndJobGroups = {
  company: CompanyDetailInOGP;
  jobGroups: JobGroup[];
};

export type CompanyEmploymentTypes = {
  offersFreelanceWork: boolean;
  offersTempWork: boolean;
  offersEmployeeWork: boolean;
};

export type CompanyEmploymentTypesForProjectForm = Pick<
  CompanyEmploymentTypes,
  'offersFreelanceWork' | 'offersTempWork'
>;

export type CompanyBasicInfo = {
  id: string;
  name: string;
};

export type CompanyListItem = CompanyListItemBasicData & {
  branchOfficesCount: number;
  branchOffices: BusinessUnitsFilterBranchOffice[];
};
type BusinessUnitsFilterBranchOffice = {
  id: string;
  name: string;
  departments: BusinessUnitsFilterDepartment[];
};

type BusinessUnitsFilterDepartment = {
  id: string;
  name: string;
};

export type CompanyFormData = CompanyDetailInOGP &
  Socials & {
    jobGroupIds: string[];
    jobGroups: JobGroup[];
  };

type JobGroup = {
  name: string;
  id: string;
};

export type Socials = {
  website: string | undefined;
  facebook: string | undefined;
  instagram: string | undefined;
  linkedin: string | undefined;
};
type LinkType = keyof Socials;
export const linkTypes: LinkType[] = ['website', 'facebook', 'instagram', 'linkedin'];

export type Link = {
  url: string | undefined;
  type: LinkType;
};

export type CompanyFormPayload = Omit<CompanyDetailInOGP, 'employmentType'> & {
  socials: Link[];
};

export type OrtListItem = {
  id: string;
  name: string;
};

export type CompanyOrt = {
  id: string;
  name: string;
  calendar: {
    workDays?: {
      monday?: OrtPeriod[];
      tuesday?: OrtPeriod[];
      wednesday?: OrtPeriod[];
      thursday?: OrtPeriod[];
      friday?: OrtPeriod[];
      saturday?: OrtPeriod[];
      sunday?: OrtPeriod[];
    };
  } | null;
  holidays: {
    [holidayName: string]: Array<{
      startTime: string;
      endTime: string;
      surcharge: number | null;
      surchargeType: 'factor' | 'offset';
    }>;
  };
  employmentType: EmploymentType;
  createdAt: string;
  utcStartDate: string;
};

export type Ort = {
  name: string;
  calendar: OrtCalendar;
};

type OrtCalendar = {
  workDays: OrtWorkDays;
};
type OrtEmploymentType = Extract<EmploymentType, 'FREELANCER' | 'TEMPWORKER'>;

export type OrtsData = {
  weekdayOrts: Ort | null;
  bankHolidayOrts: BankHolidayORT | null;
  type: OrtEmploymentType;
};

type Holiday =
  | 'Nieuwjaarsdag'
  | 'Goede Vrijdag'
  | 'Eerste Paasdag'
  | 'Tweede Paasdag'
  | 'Koningsdag'
  | 'Bevrijdingsdag elk jaar'
  | 'Bevrijdingsdag lustrumjaar'
  | 'Hemelvaartsdag'
  | 'Vrijdag na Hemelvaartsdag'
  | 'Vrijdag voorafgaand Pinksterweekend'
  | 'Eerste Pinksterdag'
  | 'Tweede Pinksterdag'
  | 'Kerstavond'
  | 'Eerste Kerstdag'
  | 'Tweede Kerstdag'
  | 'Oudejaarsavond';

export type BankHolidayORT = Partial<Record<Holiday, OrtPeriod[]>>;

type OrtWorkDays = Record<WeekDays, OrtPeriod[]>;

export type OrtPeriod = {
  startTime: string;
  endTime: string;
  surcharge: number | null;
  surchargeType: 'factor' | 'offset';
};

export type CreateCompanyOrt = {
  name: string | null;
  calendar: {
    workDays?: {
      monday?: OrtPeriod[];
      tuesday?: OrtPeriod[];
      wednesday?: OrtPeriod[];
      thursday?: OrtPeriod[];
      friday?: OrtPeriod[];
      saturday?: OrtPeriod[];
      sunday?: OrtPeriod[];
    };
  } | null;
  holidays: {
    [holidayName: string]: Array<{
      startTime: string;
      endTime: string;
      surcharge: number | null;
      surchargeType: 'factor' | 'offset';
    }>;
  };
  employmentType: EmploymentType;
  createdAt: string;
  utcStartDate: string;
  companyId: string;
};
